/**
 * Sistema de Monitoramento em Tempo Real
 * Coleta métricas, detecta anomalias e gera alertas
 */

interface Metric {
  name: string;
  value: number;
  timestamp: number;
  tags?: Record<string, string>;
}

interface Alert {
  id: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  message: string;
  timestamp: number;
  resolved: boolean;
  source: string;
}

interface HealthCheck {
  service: string;
  status: 'healthy' | 'degraded' | 'unhealthy';
  responseTime: number;
  timestamp: number;
  details?: string;
}

export class MonitoringSystem {
  private static instance: MonitoringSystem;
  private metrics: Metric[] = [];
  private alerts: Alert[] = [];
  private healthChecks: HealthCheck[] = [];
  private isMonitoring = false;
  private monitoringInterval?: NodeJS.Timeout;

  static getInstance(): MonitoringSystem {
    if (!MonitoringSystem.instance) {
      MonitoringSystem.instance = new MonitoringSystem();
    }
    return MonitoringSystem.instance;
  }

  // Iniciar monitoramento
  startMonitoring(): void {
    if (this.isMonitoring) return;

    console.log('🔍 Iniciando monitoramento em tempo real...');
    this.isMonitoring = true;

    // Coleta métricas a cada 30 segundos
    this.monitoringInterval = setInterval(() => {
      this.collectMetrics();
      this.performHealthChecks();
      this.detectAnomalies();
    }, 30000);

    // Coleta inicial
    this.collectMetrics();
    this.performHealthChecks();
  }

  // Parar monitoramento
  stopMonitoring(): void {
    if (!this.isMonitoring) return;

    console.log('⏹️ Parando monitoramento...');
    this.isMonitoring = false;

    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = undefined;
    }
  }

  // Coletar métricas do sistema
  private collectMetrics(): void {
    const timestamp = Date.now();

    // Métricas de Performance
    this.collectPerformanceMetrics(timestamp);
    
    // Métricas de Usuário
    this.collectUserMetrics(timestamp);
    
    // Métricas de Sistema
    this.collectSystemMetrics(timestamp);
    
    // Métricas de Negócio
    this.collectBusinessMetrics(timestamp);
  }

  private collectPerformanceMetrics(timestamp: number): void {
    // Tempo de carregamento da página
    if ('performance' in window) {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        this.addMetric({
          name: 'page_load_time',
          value: navigation.loadEventEnd - navigation.fetchStart,
          timestamp,
          tags: { type: 'performance' }
        });
      }

      // Uso de memória
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        this.addMetric({
          name: 'memory_usage',
          value: memory.usedJSHeapSize / 1024 / 1024, // MB
          timestamp,
          tags: { type: 'memory' }
        });

        this.addMetric({
          name: 'memory_limit',
          value: memory.jsHeapSizeLimit / 1024 / 1024, // MB
          timestamp,
          tags: { type: 'memory' }
        });
      }
    }

    // FPS (aproximado)
    let fps = 60;
    if ('requestAnimationFrame' in window) {
      let lastTime = performance.now();
      let frameCount = 0;
      
      const measureFPS = (currentTime: number) => {
        frameCount++;
        if (currentTime - lastTime >= 1000) {
          fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
          frameCount = 0;
          lastTime = currentTime;
        }
        requestAnimationFrame(measureFPS);
      };
      
      requestAnimationFrame(measureFPS);
    }

    this.addMetric({
      name: 'fps',
      value: fps,
      timestamp,
      tags: { type: 'performance' }
    });
  }

  private collectUserMetrics(timestamp: number): void {
    // Usuários ativos (simulado)
    const activeUsers = Math.floor(Math.random() * 50) + 10;
    this.addMetric({
      name: 'active_users',
      value: activeUsers,
      timestamp,
      tags: { type: 'user' }
    });

    // Sessões por página
    const currentPage = window.location.pathname;
    this.addMetric({
      name: 'page_sessions',
      value: 1,
      timestamp,
      tags: { type: 'user', page: currentPage }
    });

    // Tempo na página
    const timeOnPage = Date.now() - (window as any).pageLoadTime || 0;
    this.addMetric({
      name: 'time_on_page',
      value: timeOnPage / 1000, // segundos
      timestamp,
      tags: { type: 'user', page: currentPage }
    });
  }

  private collectSystemMetrics(timestamp: number): void {
    // Status da conexão
    const connectionStatus = navigator.onLine ? 1 : 0;
    this.addMetric({
      name: 'connection_status',
      value: connectionStatus,
      timestamp,
      tags: { type: 'system' }
    });

    // Tipo de conexão
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        this.addMetric({
          name: 'connection_speed',
          value: connection.downlink || 0,
          timestamp,
          tags: { type: 'system', effectiveType: connection.effectiveType }
        });
      }
    }

    // Erros JavaScript
    const errorCount = this.getErrorCount();
    this.addMetric({
      name: 'js_errors',
      value: errorCount,
      timestamp,
      tags: { type: 'system' }
    });
  }

  private collectBusinessMetrics(timestamp: number): void {
    // Manifestações (simulado)
    const manifestationsCount = Math.floor(Math.random() * 100) + 50;
    this.addMetric({
      name: 'manifestations_total',
      value: manifestationsCount,
      timestamp,
      tags: { type: 'business' }
    });

    // Comunidades ativas
    const activeCommunities = Math.floor(Math.random() * 20) + 15;
    this.addMetric({
      name: 'active_communities',
      value: activeCommunities,
      timestamp,
      tags: { type: 'business' }
    });

    // Taxa de resolução
    const resolutionRate = Math.random() * 0.3 + 0.7; // 70-100%
    this.addMetric({
      name: 'resolution_rate',
      value: resolutionRate,
      timestamp,
      tags: { type: 'business' }
    });
  }

  // Verificações de saúde
  private async performHealthChecks(): Promise<void> {
    const timestamp = Date.now();

    // Verificar API endpoints
    await this.checkApiHealth(timestamp);
    
    // Verificar serviços externos
    await this.checkExternalServices(timestamp);
    
    // Verificar recursos locais
    this.checkLocalResources(timestamp);
  }

  private async checkApiHealth(timestamp: number): Promise<void> {
    const endpoints = [
      '/api/manifestations',
      '/api/communities',
      '/api/analytics',
      '/api/health'
    ];

    for (const endpoint of endpoints) {
      try {
        const startTime = performance.now();
        
        // Simula verificação de API
        await new Promise(resolve => setTimeout(resolve, Math.random() * 100 + 50));
        
        const responseTime = performance.now() - startTime;
        
        this.addHealthCheck({
          service: `API${endpoint}`,
          status: responseTime < 200 ? 'healthy' : responseTime < 500 ? 'degraded' : 'unhealthy',
          responseTime,
          timestamp
        });
      } catch (error) {
        this.addHealthCheck({
          service: `API${endpoint}`,
          status: 'unhealthy',
          responseTime: 0,
          timestamp,
          details: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
  }

  private async checkExternalServices(timestamp: number): Promise<void> {
    const services = [
      { name: 'Database', url: 'db-health' },
      { name: 'Cache', url: 'cache-health' },
      { name: 'Storage', url: 'storage-health' }
    ];

    for (const service of services) {
      try {
        const startTime = performance.now();
        
        // Simula verificação de serviço externo
        await new Promise(resolve => setTimeout(resolve, Math.random() * 150 + 30));
        
        const responseTime = performance.now() - startTime;
        
        this.addHealthCheck({
          service: service.name,
          status: responseTime < 100 ? 'healthy' : responseTime < 300 ? 'degraded' : 'unhealthy',
          responseTime,
          timestamp
        });
      } catch (error) {
        this.addHealthCheck({
          service: service.name,
          status: 'unhealthy',
          responseTime: 0,
          timestamp,
          details: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
  }

  private checkLocalResources(timestamp: number): void {
    // Verificar localStorage
    try {
      localStorage.setItem('health-check', 'test');
      localStorage.removeItem('health-check');
      
      this.addHealthCheck({
        service: 'LocalStorage',
        status: 'healthy',
        responseTime: 1,
        timestamp
      });
    } catch (error) {
      this.addHealthCheck({
        service: 'LocalStorage',
        status: 'unhealthy',
        responseTime: 0,
        timestamp,
        details: 'LocalStorage not available'
      });
    }

    // Verificar sessionStorage
    try {
      sessionStorage.setItem('health-check', 'test');
      sessionStorage.removeItem('health-check');
      
      this.addHealthCheck({
        service: 'SessionStorage',
        status: 'healthy',
        responseTime: 1,
        timestamp
      });
    } catch (error) {
      this.addHealthCheck({
        service: 'SessionStorage',
        status: 'unhealthy',
        responseTime: 0,
        timestamp,
        details: 'SessionStorage not available'
      });
    }
  }

  // Detectar anomalias
  private detectAnomalies(): void {
    // Verificar métricas de performance
    this.checkPerformanceAnomalies();
    
    // Verificar métricas de sistema
    this.checkSystemAnomalies();
    
    // Verificar saúde dos serviços
    this.checkHealthAnomalies();
  }

  private checkPerformanceAnomalies(): void {
    const recentMetrics = this.getRecentMetrics('page_load_time', 5);
    if (recentMetrics.length > 0) {
      const avgLoadTime = recentMetrics.reduce((sum, m) => sum + m.value, 0) / recentMetrics.length;
      
      if (avgLoadTime > 3000) {
        this.createAlert({
          level: 'warning',
          message: `Tempo de carregamento alto: ${avgLoadTime.toFixed(0)}ms`,
          source: 'performance_monitor'
        });
      }
    }

    const memoryMetrics = this.getRecentMetrics('memory_usage', 3);
    if (memoryMetrics.length > 0) {
      const latestMemory = memoryMetrics[memoryMetrics.length - 1];
      
      if (latestMemory.value > 100) { // > 100MB
        this.createAlert({
          level: 'warning',
          message: `Alto uso de memória: ${latestMemory.value.toFixed(1)}MB`,
          source: 'memory_monitor'
        });
      }
    }
  }

  private checkSystemAnomalies(): void {
    const errorMetrics = this.getRecentMetrics('js_errors', 5);
    if (errorMetrics.length > 0) {
      const totalErrors = errorMetrics.reduce((sum, m) => sum + m.value, 0);
      
      if (totalErrors > 10) {
        this.createAlert({
          level: 'error',
          message: `Muitos erros JavaScript: ${totalErrors} nos últimos 5 minutos`,
          source: 'error_monitor'
        });
      }
    }

    const connectionMetrics = this.getRecentMetrics('connection_status', 3);
    if (connectionMetrics.length > 0) {
      const offlineCount = connectionMetrics.filter(m => m.value === 0).length;
      
      if (offlineCount > 1) {
        this.createAlert({
          level: 'warning',
          message: 'Instabilidade na conexão detectada',
          source: 'connection_monitor'
        });
      }
    }
  }

  private checkHealthAnomalies(): void {
    const recentHealthChecks = this.healthChecks.filter(
      hc => Date.now() - hc.timestamp < 5 * 60 * 1000 // últimos 5 minutos
    );

    const unhealthyServices = recentHealthChecks.filter(hc => hc.status === 'unhealthy');
    const degradedServices = recentHealthChecks.filter(hc => hc.status === 'degraded');

    if (unhealthyServices.length > 0) {
      this.createAlert({
        level: 'critical',
        message: `Serviços indisponíveis: ${unhealthyServices.map(s => s.service).join(', ')}`,
        source: 'health_monitor'
      });
    }

    if (degradedServices.length > 2) {
      this.createAlert({
        level: 'warning',
        message: `Múltiplos serviços com performance degradada: ${degradedServices.map(s => s.service).join(', ')}`,
        source: 'health_monitor'
      });
    }
  }

  // Métodos auxiliares
  private addMetric(metric: Metric): void {
    this.metrics.push(metric);
    
    // Manter apenas os últimos 1000 registros
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }
  }

  private addHealthCheck(healthCheck: HealthCheck): void {
    this.healthChecks.push(healthCheck);
    
    // Manter apenas os últimos 500 registros
    if (this.healthChecks.length > 500) {
      this.healthChecks = this.healthChecks.slice(-500);
    }
  }

  private createAlert(alertData: Omit<Alert, 'id' | 'timestamp' | 'resolved'>): void {
    const alert: Alert = {
      id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      resolved: false,
      ...alertData
    };

    this.alerts.push(alert);
    
    // Log do alerta
    const emoji = {
      info: 'ℹ️',
      warning: '⚠️',
      error: '❌',
      critical: '🚨'
    }[alert.level];

    console.log(`${emoji} ALERTA [${alert.level.toUpperCase()}]: ${alert.message}`);
    
    // Manter apenas os últimos 200 alertas
    if (this.alerts.length > 200) {
      this.alerts = this.alerts.slice(-200);
    }
  }

  private getRecentMetrics(metricName: string, minutes: number): Metric[] {
    const cutoff = Date.now() - (minutes * 60 * 1000);
    return this.metrics.filter(m => m.name === metricName && m.timestamp > cutoff);
  }

  private getErrorCount(): number {
    // Simula contagem de erros
    return Math.floor(Math.random() * 5);
  }

  // Métodos públicos para acesso aos dados
  getMetrics(metricName?: string, limit = 100): Metric[] {
    let filtered = metricName 
      ? this.metrics.filter(m => m.name === metricName)
      : this.metrics;
    
    return filtered.slice(-limit);
  }

  getAlerts(level?: Alert['level'], resolved?: boolean): Alert[] {
    return this.alerts.filter(alert => {
      if (level && alert.level !== level) return false;
      if (resolved !== undefined && alert.resolved !== resolved) return false;
      return true;
    });
  }

  getHealthChecks(service?: string): HealthCheck[] {
    return service 
      ? this.healthChecks.filter(hc => hc.service === service)
      : this.healthChecks;
  }

  resolveAlert(alertId: string): boolean {
    const alert = this.alerts.find(a => a.id === alertId);
    if (alert) {
      alert.resolved = true;
      console.log(`✅ Alerta resolvido: ${alert.message}`);
      return true;
    }
    return false;
  }

  // Dashboard de monitoramento
  getDashboardData() {
    const now = Date.now();
    const last5Minutes = now - (5 * 60 * 1000);
    
    return {
      summary: {
        totalMetrics: this.metrics.length,
        activeAlerts: this.alerts.filter(a => !a.resolved).length,
        healthyServices: this.healthChecks.filter(hc => 
          hc.timestamp > last5Minutes && hc.status === 'healthy'
        ).length,
        totalServices: new Set(this.healthChecks.map(hc => hc.service)).size
      },
      recentMetrics: {
        performance: this.getRecentMetrics('page_load_time', 5),
        memory: this.getRecentMetrics('memory_usage', 5),
        errors: this.getRecentMetrics('js_errors', 5)
      },
      alerts: this.getAlerts().slice(-10),
      healthStatus: this.getHealthChecks().slice(-20)
    };
  }
}